import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/shared/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container-main py-20">
        <div className="max-w-md mx-auto">
          {status === 'checking' || status === 'processing' ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="w-16 h-16 text-continental mx-auto mb-4 animate-spin" />
                <h1 className="text-2xl font-bold text-charcoal mb-2">Processing Payment</h1>
                <p className="text-gray-500">Please wait while we confirm your payment...</p>
              </CardContent>
            </Card>
          ) : status === 'success' ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-charcoal mb-2 font-poppins">Payment Successful!</h1>
                <p className="text-gray-500 mb-6">Thank you for your order. Your payment has been processed.</p>
                
                {paymentData && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6">
                    <p className="text-sm text-gray-500">Amount Paid</p>
                    <p className="text-2xl font-bold text-continental">
                      {paymentData.currency?.toUpperCase()} {(paymentData.amount_total / 100).toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button onClick={() => navigate('/marketplace')} variant="outline">
                    Continue Shopping
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-yellow-600" />
                </div>
                <h1 className="text-2xl font-bold text-charcoal mb-2">Payment Status Unknown</h1>
                <p className="text-gray-500 mb-6">
                  We couldn't confirm your payment. Please check your email or contact support.
                </p>
                <Button onClick={() => navigate('/dashboard')} className="btn-primary">
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
